import $ from "utils/net";
import { useSignal } from '@preact/signals';

import { OutcomeRow, OutTotoEntry, OutTotoPrize, OutTotoTournament, OutTotoTournamentMatch, OutTotoTournamentMatches, OutUserBet, OutUserBetList, TotoBetRequest, TotoOutcomeStatus, TotoTransaction } from "./types";
import { useEffect, useMemo, useState } from "preact/hooks";
import config from "../config";

export class TotocalcioService {
    constructor(
        private readonly baseApi: string,
    ) { }

    public async fetchAllTournaments(): Promise<OutTotoEntry[]> {
        const res = await $.promiseGetJSON(this.baseApi + "/sport-jackpot/get-all");
        if (res.status == 'error')
            throw res.error;
        return res.data.entries as OutTotoEntry[];
    }

    public async fetchTournament(tournament: OutTotoTournament): Promise<Record<string, TotoEvent>> {
        const res = await $.promiseGetJSON(this.baseApi + "/sport-jackpot/get/" + tournament.id);
        if (res.status == 'error')
            throw res.error;
        return this.mapTotoTournament(tournament, res.data);
    }

    private groupOdds(tournament: OutTotoTournament, event: OutTotoTournamentMatch, odds: OutTotoTournamentMatches['odds']) {
        return (odds[event.evento] || []).map(odd => ({
            code: `/odd/${event.evento}/${odd.market}/${odd.outcome}`,
            order: odd.outcome,
            name: odd.name,
            marketName: odd.marketName,
            value: odd.value
        })).reduce((c, m) => {
            const code = `/market/${m.code.split('/').slice(2, 4).join('/')}`;
            c[code] = c[code] || {
                code: code,
                name: m.marketName,
                sub: {},
            };
            c[code].sub[m.code] = m;
            return c;
        }, {} as TotoEvent['sub'])
    }

    private readSettlement = (event) => {
        return JSON.parse(event.ftHome)['@CASA'] + ':' + JSON.parse(event.ftAway)['@FUORI'];
    }

    private normalizeMatch = (event: OutTotoTournamentMatch) => ({
        code: `/event/${event.evento}`,
        name: event.home + ' - ' + event.away,
        time: new Date(event.time),

        home: event.home,
        away: event.away,

        result: event.ftHome ? this.readSettlement(event) : null,

        parent: {
            code: event.tournamentCode,
            name: event.tournamentName,
            label: event.tournamentName,
            parent: {
                code: `/category/${event.tournamentCode.split('/').slice(2, 4).join('/')}`,
                name: event.categoryName,
                label: event.categoryName,
                parent: {
                    code: `/category/${event.tournamentCode.split('/')[2]}`,
                    name: "Soccer",
                    label: "Soccer",
                }
            }
        },

        sub: {}
    } satisfies TotoEvent)

    private mapTotoTournament(tournament: OutTotoTournament, totoTournament: OutTotoTournamentMatches) {
        const { matches } = totoTournament;
        const matchDb: Record<string, TotoEvent> = matches.map(event => ({
            ...this.normalizeMatch(event),
            sub: this.groupOdds(tournament, event, totoTournament.odds)
        })).reduce((c, m) => (c[m.code] = m, c), {});
        return matchDb;
    }

    public async placeBet(body: TotoBetRequest): Promise<TotoTransaction> {
        const res = await $.promisePostJSON(this.baseApi + "/sport-jackpot/place", {
            body: JSON.stringify(body)
        });
        if (res.status == 'error')
            throw res.error;
        return res.data as TotoTransaction;
    }

    private normalizeBet = (bet: OutUserBet, outcomes: Record<string, OutcomeRow>, events: Record<string, TotoEvent>) => ({
        bet,
        selections: bet.selections.map(selection => ({
            ...selection,
            event: events[`/event/${selection.code.split('-')[0]}`],
            outcomeName: outcomes[selection.code.split('-').slice(1).join('-')].name,
            marketName: outcomes[selection.code.split('-').slice(1).join('-')].marketName,
        }))
    })

    public async getMyBets(): Promise<UserBet[]> {
        const res = await $.promiseGetJSON(this.baseApi + "/sport-jackpot/my-bets");
        if (res.status == 'error')
            throw res.error;
        const data = res.data as OutUserBetList;
        const events: Record<string, TotoEvent> = data.matches.map(this.normalizeMatch).reduce((c, event) => (c[event.code] = event, c), {});
        const outcomes: Record<string, OutcomeRow> = data.outcomes.reduce((c, row) => (c[row.market+'-'+row.outcome] = row, c), {});
        return data.bets.map(bet => this.normalizeBet(bet, outcomes, events));
    }
}

export interface TotoEvent {
    code: string;
    name: string;
    time: Date;

    home: string;
    away: string;

    result?: string;

    parent: {
        code: string;
        name: string;
        label: string;
        parent: {
            code: string;
            name: string;
            label: string;
            parent: {
                code: string;
                name: string;
                label: string;
            }
        }
    },

    sub: Record<string, {
        code: string;
        name: string;
        sub: Record<string, {
            code: string;
            name: string;
            value: number;
        }>
    }>
}

export interface TicketOdd {
    code: string;
    name: string;
    parent: TicketMarket
}
export interface TicketMarket {
    code: string;
    name: string;
    parent: TicketEvent
}
export interface TicketEvent {
    code: string;
    name: string;
    parent: TotoEvent['parent']
}

export interface UserBet {
    bet: OutUserBet;
    selections: {
        event: TotoEvent;
        outcomeName: string;
        marketName: string;
        code: String;
        status: TotoOutcomeStatus;
    }[];
}

/**
 * è un ticket, ma il bet amount e la win sono fissi.
 */
export class TotoTicket {
    public odds: TicketOdd[] = [];
    private readonly selectedOdds: Record<string, TicketOdd> = {};
    private listeners: (() => void)[] = [];

    public placing = false;
    public lastPlacingResult?: { status: 'success', data: TotoTransaction } | { status: 'error', error: string } = null;

    constructor(
        private readonly totocalcioService: TotocalcioService,
        private readonly tournamentInfo: OutTotoTournament,
        private readonly tournamentPrizes: OutTotoPrize[],
        private readonly data: Record<string, TotoEvent>,
    ) { }

    addListener(cb: () => void) {
        this.listeners.push(cb);
    }
    removeListener(cb: () => void) {
        this.listeners = this.listeners.filter(c => c != cb);
    }

    private trigger() {
        this.listeners.forEach(cb => {
            try {
                cb();
            } catch (e) {
                console.error(e)
            }
        })
    }

    getBetAmount() {
        return this.tournamentInfo.displayCost;
    }

    readOddFromCode(oddCode: string): TicketOdd {
        const [_1, _2, evento, scommessa, esito] = oddCode.split('/');
        const eventCode = `/event/${evento}`;
        const marketCode = `/market/${evento}/${scommessa}`;
        const event = this.data[eventCode];
        const market = event.sub[marketCode];
        const odd = market.sub[oddCode];
        return {
            code: oddCode,
            name: odd.name,
            parent: {
                code: market.code,
                name: market.name,
                parent: {
                    code: event.code,
                    name: event.name,
                    parent: event.parent
                }
            }
        }
    }

    /**
     * @param oddCode "/odd/123456/3/1"
     */
    toggleOdd(oddCode: string) {
        if (this.placing)
            return;
        this.lastPlacingResult = null;

        const oddInfo = this.readOddFromCode(oddCode);
        if (this.hasOdd(oddCode)) {
            delete this.selectedOdds[oddCode];
            this.odds = this.odds.filter(odd => odd.code != oddCode);
        } else {
            if (!this.canAddOdd(oddCode)) {
                this.conflictStrategy(oddInfo);
            } else {
                this.selectedOdds[oddCode] = oddInfo;
                this.odds = [...this.odds, oddInfo];
            }
        }
        this.recalcTicket();
    }

    hasOdd(oddCode: string) {
        return this.selectedOdds[oddCode] != null;
    }

    canAddOdd(oddCode: string) {
        const matchId = oddCode.split('/')[2];
        return Object.keys(this.selectedOdds).every(code => code.split('/')[2] != matchId)
    }

    conflictStrategy(addOdd: TicketOdd) {
        const eventCode = addOdd.parent.parent.code;
        const conflictOdd = Object.values(this.selectedOdds).filter(odd => odd.parent.parent.code == eventCode);
        for (let odd of conflictOdd) {
            delete this.selectedOdds[odd.code];
            this.odds = this.odds.filter(tOdd => tOdd.code != odd.code);
        }
        this.selectedOdds[addOdd.code] = addOdd;
        this.odds = [...this.odds, addOdd];
    }

    isPlayable(): boolean {
        return new Date(this.tournamentInfo.startDate).getTime() > Date.now()
            && this.tournamentPrizes.find(r => this.odds.length >= r.minSelections) != null
    }

    maxWin(): OutTotoPrize {
        return this.tournamentPrizes.filter(r => this.odds.length >= r.minSelections).sort((a, b) => b.displayPrize - a.displayPrize)[0]
    }

    recalcTicket() {
        // TODO (in effetti qui non c'è nulla da fare)
        this.trigger();
    }

    async placeBet() {
        this.placing = true;
        try {
            const response = await this.totocalcioService.placeBet({
                totoTournamentId: this.tournamentInfo.id,
                odds: this.odds.map(odd => odd.code.split('/').slice(2).join('-'))
            });
            this.placing = false;
            this.lastPlacingResult = {
                status: 'success',
                data: response
            };
            this.trigger();
            return response;
        } catch (e) {
            this.placing = false;
            this.lastPlacingResult = {
                status: 'error',
                error: e
            };
            this.trigger();
        }
    }

    randomPick() {
        // Se tutte le partite hanno una quota selezionata, le toglie e parte come se non ci fosse nulla.
        const uniqueEventIds = new Set(Object.values(this.selectedOdds).map(odd => odd.parent.parent.code));
        if (uniqueEventIds.size == Object.values(this.data).length) {
            for (const key of Object.keys(this.selectedOdds))
                delete this.selectedOdds[key];
            this.odds = [];
        }

        // Prende una quota casuale solo per le partite ancora NON selezionate.
        const selectedEvents = new Set(Object.values(this.selectedOdds).map(odd => odd.parent.parent.code));
        for (let match of Object.values(this.data)) {
            if (selectedEvents.has(match.code))
                continue;
            const odds = Object.keys(Object.values(match.sub)[0].sub)
            const randomOdd = odds[Math.floor(odds.length * Math.random())]
            this.toggleOdd(randomOdd);
        }
    }
}

export function useTotocalcioApi() {
    const api = useMemo(() => new TotocalcioService(config.softionAPI), [config.softionAPI])
    return api;
}

export function useTotocalcioTournamentsList(api: TotocalcioService, filter: 'all' | 'pending' | 'open' | 'completed') {
    const [tournamentsList, setTournamentsList] = useState<OutTotoEntry[]>([]);
    const [isLoading, setLoading] = useState<boolean>(true);

    const filteredList = useMemo(() => {
        return tournamentsList.filter(t =>
            filter == 'all' ? true
                : filter == 'open' ? new Date(t.tournament.startDate) > new Date() && new Date(t.tournament.endDate) > new Date()
                    : filter == 'pending' ? new Date(t.tournament.startDate) < new Date() && new Date(t.tournament.endDate) > new Date()
                        : filter == 'completed' ? new Date(t.tournament.endDate) < new Date()
                            : false);
    }, [filter, tournamentsList]);

    useEffect(() => {
        api.fetchAllTournaments().then(setTournamentsList).then(() => setLoading(false));
    }, [api]);

    return {
        filter,
        filteredList,
        tournamentsList,
        isLoading,
    }
}

export function useTotocalcioTournament(api: TotocalcioService, tournamentId: OutTotoTournament) {
    const [tournamentInfo, setTournamentInfo] = useState<Record<string, TotoEvent>>(null);
    const [isLoading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (tournamentId == null)
            return;
        api.fetchTournament(tournamentId).then(setTournamentInfo).then(() => setLoading(false));
        return () => {
            setLoading(true);
            setTournamentInfo(null);
        }
    }, [api, tournamentId]);

    return {
        tournamentInfo,
        isLoading,
    }
}

export function useTotocalcioTicket(totoTicket: TotoTicket | null) {
    const odds = useSignal<TicketOdd[]>([]);
    const betAmount = useSignal<number>(0);
    const isPlayable = useSignal<boolean>(false);
    const isPlacing = useSignal<boolean>(false);
    const lastPlacingResult = useSignal<TotoTicket['lastPlacingResult']>(null);

    const handleUpdate = () => {
        if (totoTicket == null) {
            odds.value = [];
            betAmount.value = 0;
            isPlayable.value = false;
            isPlacing.value = false;
            lastPlacingResult.value = null;
            return;
        }
        odds.value = totoTicket.odds;
        betAmount.value = totoTicket.getBetAmount();
        isPlayable.value = totoTicket.isPlayable();
        isPlacing.value = totoTicket.placing;
        lastPlacingResult.value = totoTicket.lastPlacingResult;
    }

    useEffect(() => {
        handleUpdate();
        if (totoTicket == null)
            return;
        totoTicket.addListener(handleUpdate);
        return () => totoTicket.removeListener(handleUpdate);
    }, [totoTicket]);

    return {
        odds,
        betAmount,
        isPlayable,
        isPlacing,
        lastPlacingResult,
    }
}